import type { GetInvoicesOverviewsResponse } from '@weft/api-definition';
import { GetInvoicesOverviewsResponseSchema } from '@weft/api-definition';
import type { WeftApi } from '@weft/client';
import type { QueryClient, UseQueryOptions } from 'react-query';
import { useQuery, useQueryClient } from 'react-query';
import { useInvoicesPageParams } from '../../../pages/invoices/InvoicesPage/invoices-page-hooks/use-invoices-page-params';
import { getPrivateApi } from '../../../services/api';
import { USE_INVOICES_OVERVIEWS_QUERY_KEY } from './use-invoices-overviews-consts';

type UseInvoicesOverviewsPayload = Parameters<WeftApi['getInvoicesOverviews']>[0];

export function useInvoicesOverviews(payload: UseInvoicesOverviewsPayload = {}, options?: UseQueryOptions<GetInvoicesOverviewsResponse>) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: [USE_INVOICES_OVERVIEWS_QUERY_KEY, payload],
    queryFn: async ({ signal }) => {
      const api = getPrivateApi();
      await cancelGetInvoicesOverviewsQueries(queryClient);

      const getInvoicesOverviewsResponse = await api.getInvoicesOverviews(
        payload,
        {
          signal,
        });

      return GetInvoicesOverviewsResponseSchema.parse(getInvoicesOverviewsResponse);
    },
    ...options,
  });
}

export function useInvoicesOverviewsFromParams(options?: UseQueryOptions<GetInvoicesOverviewsResponse>) {
  const payload = useInvoicesOverviewsPayloadFromParams();
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: [USE_INVOICES_OVERVIEWS_QUERY_KEY, payload],
    queryFn: async ({ signal }) => {
      const api = getPrivateApi();
      await cancelGetInvoicesOverviewsQueries(queryClient);

      const getInvoicesOverviewsResponse = await api.getInvoicesOverviews(
        payload,
        {
          signal,
        });

      return GetInvoicesOverviewsResponseSchema.parse(getInvoicesOverviewsResponse);
    },
    ...options,
  });
}

export function useInvoicesOverviewsPayloadFromParams(): UseInvoicesOverviewsPayload {
  const {
    selectedWorkflows,
    search,
    selectedBillingStatuses,
    dates,
    page,
    pageSize,
    selectedAdditionalFilters,
  } = useInvoicesPageParams();

  return {
    search,
    dates: JSON.stringify(dates),
    billingStatuses: JSON.stringify(selectedBillingStatuses),
    reviewStatuses: JSON.stringify(selectedWorkflows),
    page,
    pageSize,
    additionalFilters: JSON.stringify(selectedAdditionalFilters),
  };
}

async function cancelGetInvoicesOverviewsQueries(queryClient: QueryClient) {
  await queryClient.cancelQueries({
    predicate: (query) => query.queryKey[0] === USE_INVOICES_OVERVIEWS_QUERY_KEY,
  });
}